import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { toNumber } from '@mahawi/eshop-common/src/to-number';
import { type ICurrencyType } from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
} from 'antd';
import {
  type ColumnFilterItem,
  type ColumnType,
} from 'antd/es/table/interface';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DeliveryFeeAdd from '../components/delivery/delivery-fee-add';
import DetailPageHeader from '../components/detail-page-header';
import Table from '../components/table';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  countryLoad,
  countrySaveCurrency,
  countrySaveDeliveryDescription,
  countrySaveDeliveryTime,
  countrySaveDescription,
  countrySaveOriginCurrency,
} from '../reducers/country/reducer';
import {
  deliveryDelete,
  deliveryLoadByCountry,
} from '../reducers/delivery/reducer';
import { type IDeliveryRecord } from '../reducers/delivery/types';
import { SPACING } from '../theme/tokens';

function SettingsCountryContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const countryCountry = useSelector((s: RootState) => s.Country.country);
  const currencyAllCurrencies = useSelector(
    (s: RootState) => s.Currency.allCurrencies,
  );
  const currencyCurrencies = useSelector(
    (s: RootState) => s.Currency.currencies,
  );
  const deliveryDeliveries = useSelector(
    (s: RootState) => s.Delivery.deliveries,
  );
  const deliveryInProcess = useSelector((s: RootState) => s.Delivery.inProcess);

  const { isoCode } = useParams();

  const [description, setDescription] = useState<string>('');
  const [deliveryDescription, setDeliveryDescription] = useState<string>('');
  const [deliveryTime, setDeliveryTime] = useState<string>('');
  const [currencyIsoCode, setCurrencyIsoCode] = useState<string>('');
  const [originCurrencyIsoCode, setOriginCurrencyIsoCode] = useState<
    string | null
  >(null);
  const [editingDelivery, setEditingDelivery] =
    useState<IDeliveryRecord | null>(null);

  useEffect((): void => {
    if (isoCode) {
      dispatch(countryLoad(isoCode));
      dispatch(deliveryLoadByCountry(isoCode));
    }
  }, [isoCode, dispatch]);

  useEffect((): void => {
    if (countryCountry?.currencyType?.isoCode) {
      setCurrencyIsoCode(countryCountry.currencyType.isoCode);
    }

    setOriginCurrencyIsoCode(
      countryCountry?.originCurrencyType?.isoCode ?? null,
    );
  }, [countryCountry]);

  const deliveryColumns: ColumnType<object>[] = [
    {
      title: 'Currency',
      dataIndex: 'currencyTypeIsoCode',
      sorter: (a: IDeliveryRecord, b: IDeliveryRecord): number =>
        stringCompare(a.currencyTypeIsoCode, b.currencyTypeIsoCode),
      filterSearch: true,
      filters: [
        ...new Set(
          (deliveryDeliveries ?? []).map(
            ({ currencyTypeIsoCode }: IDeliveryRecord): string =>
              currencyTypeIsoCode,
          ),
        ),
      ].map((currencyTypeIsoCode: string): ColumnFilterItem => ({
        text: currencyTypeIsoCode,
        value: currencyTypeIsoCode,
      })),
      onFilter: (value: string, record: IDeliveryRecord): boolean =>
        record.currencyTypeIsoCode === value,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight',
      sorter: (a: IDeliveryRecord, b: IDeliveryRecord): number =>
        numberCompare(a.weight, b.weight),
      filters: [
        ...new Set(
          (deliveryDeliveries ?? []).map(
            ({ weight }: IDeliveryRecord): number => weight,
          ),
        ),
      ].map((weight: number): ColumnFilterItem => ({
        text: String(weight),
        value: weight,
      })),
      onFilter: (value: number, record: IDeliveryRecord): boolean =>
        record.weight === value,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      sorter: (a: IDeliveryRecord, b: IDeliveryRecord): number =>
        numberCompare(a.price, b.price),
    },
    {
      title: 'Price without tax',
      dataIndex: 'priceWithoutTax',
      sorter: (a: IDeliveryRecord, b: IDeliveryRecord): number =>
        numberCompare(a.priceWithoutTax, b.priceWithoutTax),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_text: string, record: IDeliveryRecord): React.ReactElement => (
        <Space size={SPACING.sm}>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={deliveryInProcess}
            onClick={(): void => setEditingDelivery(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this delivery fee?"
            okText="Yes"
            cancelText="No"
            onConfirm={(): void => {
              if (!isoCode) {
                return;
              }
              dispatch(deliveryDelete(record.id, isoCode));
              if (editingDelivery?.id === record.id) {
                setEditingDelivery(null);
              }
            }}
            disabled={deliveryInProcess}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={deliveryInProcess}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const deliveryDataSource: (IDeliveryRecord & { key: number })[] = (
    deliveryDeliveries ?? []
  ).map((delivery: IDeliveryRecord) => ({ ...delivery, key: delivery.id }));

  if (countryCountry === null) {
    return (
      <Alert
        title="Error"
        description="Country not found"
        type="error"
        showIcon
      />
    );
  }

  if (countryCountry === undefined) {
    return <Spin size="large" spinning />;
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title={`Country ${isoCode ?? ''}`} />

      <Descriptions
        bordered
        column={3}
        items={[
          {
            key: 'name',
            label: 'Name',
            children: countryCountry.name,
          },
          {
            key: 'iso-code',
            label: 'ISO Code',
            children: countryCountry.isoCode,
          },
          {
            key: 'iso-code-2',
            label: 'ISO Code 2',
            children: countryCountry.isoCode2,
          },
        ]}
      />

      <Form layout="vertical">
        <Form.Item label="Description" tooltip="Shown on the delivery page.">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              defaultValue={countryCountry.description || ''}
              placeholder="Description of the country."
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setDescription(e.target.value)
              }
            />
            <Popconfirm
              placement="bottom"
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                dispatch(
                  countrySaveDescription(countryCountry.isoCode, description),
                );
              }}
            >
              <Button type="primary">Save</Button>
            </Popconfirm>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Delivery description"
          tooltip="Shown on the delivery page."
        >
          <Space.Compact style={{ width: '100%' }}>
            <Input
              defaultValue={countryCountry.delivery.description || ''}
              placeholder="Parcel services, restrictions, etc."
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setDeliveryDescription(e.target.value)
              }
            />
            <Popconfirm
              placement="bottom"
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                dispatch(
                  countrySaveDeliveryDescription(
                    countryCountry.isoCode,
                    deliveryDescription,
                  ),
                );
              }}
            >
              <Button type="primary">Save</Button>
            </Popconfirm>
          </Space.Compact>
        </Form.Item>

        <Form.Item label="Delivery time" tooltip="Shown on the delivery page.">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              defaultValue={countryCountry.delivery.time || ''}
              placeholder="Delivery time approx. in days."
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setDeliveryTime(e.target.value)
              }
              type="number"
            />
            <Button type="default" style={{ pointerEvents: 'none' }}>
              business days
            </Button>
            <Popconfirm
              placement="bottom"
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                dispatch(
                  countrySaveDeliveryTime(
                    countryCountry.isoCode,
                    toNumber(deliveryTime),
                  ),
                );
              }}
            >
              <Button type="primary">Save</Button>
            </Popconfirm>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Origin currency"
          tooltip="The currency physically used in this country."
        >
          <Space.Compact style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              value={originCurrencyIsoCode ?? undefined}
              onChange={(value: string | undefined): void =>
                setOriginCurrencyIsoCode(value ?? null)
              }
              options={currencyAllCurrencies.map(
                ({ isoCode: cIsoCode, name }: ICurrencyType) => ({
                  value: cIsoCode,
                  label: `${cIsoCode} — ${name}`,
                }),
              )}
            />
            <Popconfirm
              placement="bottom"
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                dispatch(
                  countrySaveOriginCurrency(
                    countryCountry.isoCode,
                    originCurrencyIsoCode,
                  ),
                );
              }}
            >
              <Button type="primary">Save</Button>
            </Popconfirm>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Default currency"
          tooltip="Default currency used for carrier parcel creation."
        >
          <Space.Compact style={{ width: '100%' }}>
            <Select
              showSearch
              style={{ width: '100%' }}
              value={currencyIsoCode || undefined}
              onChange={(value: string): void => setCurrencyIsoCode(value)}
              options={currencyCurrencies.map(
                ({ isoCode: cIsoCode, name }: ICurrencyType) => ({
                  value: cIsoCode,
                  label: `${cIsoCode} — ${name}`,
                }),
              )}
            />
            <Popconfirm
              placement="bottom"
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                dispatch(
                  countrySaveCurrency(countryCountry.isoCode, currencyIsoCode),
                );
              }}
            >
              <Button type="primary">Save</Button>
            </Popconfirm>
          </Space.Compact>
        </Form.Item>
      </Form>

      <Collapse
        items={[
          {
            key: 'delivery-fees',
            label: 'Delivery fees',
            children: (
              <Space
                size={SPACING.lg}
                orientation="vertical"
                style={{ width: '100%' }}
              >
                <DeliveryFeeAdd
                  countryTypeIsoCode={countryCountry.isoCode}
                  editingDelivery={editingDelivery}
                  onEditSubmitted={(): void => setEditingDelivery(null)}
                />

                <Table
                  columns={deliveryColumns}
                  dataSource={deliveryDataSource}
                  isLoading={deliveryInProcess && !deliveryDeliveries}
                />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}

export default SettingsCountryContainer;
